import { PLATFORM_ROOT_ACCESS_PROFILE } from './platform-root.access-profile.js';
import { PLATFORM_ADMIN_ACCESS_PROFILE } from './platform-admin.access-profile.js';
import { PLATFORM_USER_ACCESS_PROFILE } from './platform-user.access-profile.js';
import { ORGANIZATION_ADMIN_ACCESS_PROFILE } from './organization-admin.access-profile.js';
import { ORGANIZATION_USER_ACCESS_PROFILE } from './organization-user.access-profile.js';
import { ORGANIZATION_CLIENT_ACCESS_PROFILE } from './organization-client.access-profile.js';
import { SUPPORT_DELEGATION_OVERLAY } from './support-delegation.overlay.js';
import { CANONICAL_PERSONAS, resolvePersonaPermissions } from '../canonical-personas.js';
import { resolveOperationBundle } from '../operation-bundles/index.js';
import { CANONICAL_OPERATIONS_REGISTRY } from '../../operations/index.js';
import { EFFECTIVE_PERMISSION_REGISTRY as PERMISSION_REGISTRY } from '../effective-permission.registry.js';
export const PERSONA_ACCESS_PROFILE_REGISTRY = {
    PLATFORM_ROOT: PLATFORM_ROOT_ACCESS_PROFILE,
    PLATFORM_ADMIN: PLATFORM_ADMIN_ACCESS_PROFILE,
    PLATFORM_USER: PLATFORM_USER_ACCESS_PROFILE,
    ORG_ADMIN: ORGANIZATION_ADMIN_ACCESS_PROFILE,
    ORG_USER: ORGANIZATION_USER_ACCESS_PROFILE,
    ORG_CLIENT: ORGANIZATION_CLIENT_ACCESS_PROFILE,
};
export const SUPPORT_DELEGATION_OVERLAY_DEFINITION = SUPPORT_DELEGATION_OVERLAY;
function requireBundle(bundleId, persona) {
    const bundle = resolveOperationBundle(bundleId);
    if (!bundle) {
        throw new Error(`Missing operation bundle ${bundleId}${persona ? ` for ${persona}` : ''}`);
    }
    return bundle;
}
function requireCanonicalBundleOperations(bundleId, persona) {
    const bundle = requireBundle(bundleId, persona);
    for (const operationId of bundle.operationIds) {
        if (!CANONICAL_OPERATIONS_REGISTRY[operationId]) {
            throw new Error(`Operation ${String(operationId)} in bundle ${bundleId} is not canonical`);
        }
    }
}
export function validatePersonaAccessProfile(profile) {
    if (!CANONICAL_PERSONAS.includes(profile.persona)) {
        throw new Error(`Unknown persona ${profile.persona}`);
    }
    for (const bundleId of profile.access.allowedOperationBundleIds) {
        requireCanonicalBundleOperations(bundleId, profile.persona);
    }
    for (const bundleId of profile.access.defaultOperationBundleIds) {
        if (!profile.access.allowedOperationBundleIds.includes(bundleId)) {
            throw new Error(`Default bundle ${bundleId} for ${profile.persona} is not in allowed bundles`);
        }
        requireCanonicalBundleOperations(bundleId, profile.persona);
    }
}
export function getPersonaAccessProfile(persona) {
    const profile = PERSONA_ACCESS_PROFILE_REGISTRY[persona];
    if (!profile) {
        throw new Error(`Persona access profile not found: ${persona}`);
    }
    return profile;
}
export function getAllPersonaAccessProfiles() {
    return CANONICAL_PERSONAS.map((persona) => PERSONA_ACCESS_PROFILE_REGISTRY[persona]);
}
function getPermissionIdsForBundleIds(bundleIds) {
    const permissionIds = new Set();
    for (const bundleId of bundleIds) {
        const bundle = requireBundle(bundleId);
        for (const operationId of bundle.operationIds) {
            const descriptor = CANONICAL_OPERATIONS_REGISTRY[operationId];
            if (!descriptor) {
                throw new Error(`Operation ${String(operationId)} in bundle ${bundleId} is not canonical`);
            }
            const permissionId = descriptor.permissionId ?? descriptor.permission;
            if (permissionId && permissionId in PERMISSION_REGISTRY) {
                permissionIds.add(permissionId);
            }
        }
    }
    return permissionIds;
}
/**
 * Returns permission references that cannot yet be projected into a session
 * grant. This makes broader control-plane debt observable instead of silently
 * treating those strings as valid session permissions.
 */
export function getPersonaUnregisteredOperationPermissions(persona) {
    const profile = getPersonaAccessProfile(persona);
    const issues = [];
    for (const bundleId of profile.access.allowedOperationBundleIds) {
        const bundle = requireBundle(bundleId, persona);
        for (const operationId of bundle.operationIds) {
            const descriptor = CANONICAL_OPERATIONS_REGISTRY[operationId];
            if (!descriptor)
                continue;
            const permissionId = descriptor.permissionId ?? descriptor.permission;
            if (permissionId && !(permissionId in PERMISSION_REGISTRY)) {
                issues.push({ bundleId, operationId, permissionId });
            }
        }
    }
    return issues;
}
export function getEffectivePersonaPermissionIds(persona) {
    const profile = getPersonaAccessProfile(persona);
    return Array.from(getPermissionIdsForBundleIds(profile.access.allowedOperationBundleIds));
}
export function detectPersonaPermissionDrift(persona, effectivePermissions) {
    const profile = getPersonaAccessProfile(persona);
    // The persistence reconciler owns a system role through the canonical
    // permission bundle. Operation bundles are an additional routing and
    // descriptor projection, but may lag a registered permission while an
    // operation is being introduced. Include both sources so a reconciled role
    // is not reported as having elevated permissions merely because it has no
    // corresponding route descriptor yet.
    const canonical = resolvePersonaPermissions(persona);
    const allowed = new Set([
        ...canonical,
        ...getPermissionIdsForBundleIds(profile.access.allowedOperationBundleIds),
    ]);
    const defaults = new Set([
        ...canonical,
        ...getPermissionIdsForBundleIds(profile.access.defaultOperationBundleIds),
    ]);
    const effective = new Set(effectivePermissions);
    return {
        extra: Array.from(effective).filter((permission) => !allowed.has(permission)),
        missing: Array.from(defaults).filter((permission) => !effective.has(permission)),
    };
}
